import React, { useMemo, useState, useRef, useEffect } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination, EffectFade } from "swiper/modules";
import "swiper/css";
import "swiper/css/effect-fade";
import "swiper/css/pagination";
import {
  faDownload,
  faAd,
  faPlay,
  faTimes,
  faCheck,
  faArrowLeft,
  faUsers,
  faGraduationCap,
  faShieldAlt,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import VideoViewerV2 from "../Shared/VideoViewerV2";
import { getFullFileUrl } from "../../Helpers/UrlHelpers";
import banner1 from "../../assets/images/banner1.png";
import banner2 from "../../assets/images/banner2.png";
import banner3 from "../../assets/images/banner3.png";
import videoScreen from "../../assets/images/video_screen.png";

const MOCK_BANNER_ADS = [
  {
    id: 1,
    image: banner1,
    title: "حمل تطبيق لوحة تحكم <em>امتحاني للأندرويد</em> الآن",
    category: "تطبيق لوحة تحكم امتحاني",
    description:
      "قم بإدارة اختباراتك وطلابك بسهولة من خلال تطبيق لوحة تحكم امتحاني. حمل التطبيق بصيغة APK مباشرة على جهازك واستمتع بتجربة إدارة متكاملة وسهلة.",
    primaryButton: { text: "تحميل التطبيق للمعلمين", icon: faDownload, action: () => { } },
    secondaryButton: { text: "شاهد الفيديو التعريفي", icon: faPlay, action: () => { } },
  },
  {
    id: 2,
    image: banner2,
    title: "مع منصة امتحاني <em>اختباراتك اصبحت اسهل</em>",
    category: "منصة امتحاني",
    description:
      "منصة امتحاني هي منصة تعليمية تساعد الطلاب في عمل اختبارات عبر الانترنت دون الحاجة لمعلم",
    primaryButton: { text: "شاهد الفيديو التعريفي", icon: faPlay, action: () => { } },
  },
  {
    id: 3,
    image: banner3,
    title: "احصل على <em>نتائجك فوراً</em> مع تحليل مفصل",
    category: "نتائج فورية",
    description:
      "مع منصة امتحاني، احصل على نتائجك فوراً مع تحليل مفصل لأدائك وتوصيات لتحسين مهاراتك بشكل مستمر.",
    primaryButton: { text: "ابدأ الآن", icon: faCheck, action: () => { } },
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.15, ease: [0.21, 1, 0.36, 1] },
  }),
};

const isVideo = (url) => {
  if (typeof url !== "string") return false;
  const cleanUrl = url.split("?")[0].split("#")[0];
  return [".mp4", ".webm", ".ogg", ".mov", ".avi", ".mkv", ".m4v", ".3gp"].some((e) =>
    cleanUrl.toLowerCase().endsWith(e)
  );
};

/* ─── Interactive Particle Background ─── */
const ParticleBackground = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animationFrameId;
    let particles = [];

    // Mouse tracking
    let mouse = { x: -1000, y: -1000 };

    const handleMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouse.x = e.clientX - rect.left;
      mouse.y = e.clientY - rect.top;
    };

    const handleMouseLeave = () => {
      mouse.x = -1000;
      mouse.y = -1000;
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseout', handleMouseLeave);

    class Particle {
      constructor(x, y) {
        this.baseX = x;
        this.baseY = y;
        this.x = x;
        this.y = y;
        this.vx = 0;
        this.vy = 0;
        this.size = Math.random() * 1.5 + 1;
      }

      update() {
        let dx = mouse.x - this.x;
        let dy = mouse.y - this.y;
        let distance = Math.sqrt(dx * dx + dy * dy);

        let maxDist = 220;
        let minDist = 40;

        if (distance < maxDist && distance > 0) {
          if (distance < minDist) {
            // Repel strongly if too close to center
            let force = (minDist - distance) / minDist;
            this.vx -= (dx / distance) * force * 4;
            this.vy -= (dy / distance) * force * 4;
          } else {
            // Attract towards cursor
            let force = (maxDist - distance) / maxDist;
            this.vx += (dx / distance) * force * 2;
            this.vy += (dy / distance) * force * 2;

            // Add a slight tangential swirl
            this.vx += (dy / distance) * force * 0.5;
            this.vy -= (dx / distance) * force * 0.5;
          }
        } else {
          // Spring back to base position
          let bx = this.baseX - this.x;
          let by = this.baseY - this.y;
          this.vx += bx * 0.02;
          this.vy += by * 0.02;
        }

        // Friction
        this.vx *= 0.82;
        this.vy *= 0.82;

        this.x += this.vx;
        this.y += this.vy;
      }

      draw(ctx) {
        ctx.fillStyle = 'rgba(99, 102, 241, 0.45)';
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.closePath();
        ctx.fill();
      }
    }

    const initParticles = () => {
      particles = [];
      const density = 1500; // px per particle
      let numParticles = Math.floor((canvas.width * canvas.height) / density);
      if (numParticles > 1200) numParticles = 1200;
      if (numParticles < 200) numParticles = 200;

      for (let i = 0; i < numParticles; i++) {
        let x = Math.random() * canvas.width;
        let y = Math.random() * canvas.height;
        particles.push(new Particle(x, y));
      }
    };

    const resize = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      canvas.width = parent.clientWidth;
      canvas.height = parent.clientHeight;
      initParticles();
    };

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (let i = 0; i < particles.length; i++) {
        particles[i].update();
        particles[i].draw(ctx);
      }
      animationFrameId = requestAnimationFrame(animate);
    };

    // Delay initial resize to ensure DOM is ready
    setTimeout(() => {
      resize();
      animate();
    }, 50);

    window.addEventListener('resize', resize);

    return () => {
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseout', handleMouseLeave);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 0
      }}
    />
  );
};

/* ─── Trust Badges Row ─── */
const TrustBadges = ({ studentCount, teacherCount }) => (
  <motion.div
    className="mb-trust-row"
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.7, delay: 0.9 }}
  >
    <div className="mb-trust-badge">
      <div className="mb-trust-icon">
        <FontAwesomeIcon icon={faUsers} />
      </div>
      <span>+{studentCount} طالب نشط</span>
    </div>
    <div className="mb-trust-sep" />
    <div className="mb-trust-badge">
      <div className="mb-trust-icon">
        <FontAwesomeIcon icon={faGraduationCap} />
      </div>
      <span>+{teacherCount} معلم معتمد</span>
    </div>
    <div className="mb-trust-sep" />
    <div className="mb-trust-badge">
      <div className="mb-trust-icon">
        <FontAwesomeIcon icon={faShieldAlt} />
      </div>
      <span>حماية متقدمة</span>
    </div>
  </motion.div>
);

const MainBanner = ({ teacherAds = [], studentCount = 5000, teacherCount = 100 }) => {
  const navigate = useNavigate();
  const swiperRef = useRef(null);
  const [showVideo, setShowVideo] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouse = (e) => {
      setMousePos({
        x: (e.clientX / window.innerWidth - 0.5) * 20,
        y: (e.clientY / window.innerHeight - 0.5) * 20,
      });
    };
    window.addEventListener("mousemove", handleMouse);
    return () => window.removeEventListener("mousemove", handleMouse);
  }, []);

  const bannerData = useMemo(() => {
    if (!Array.isArray(teacherAds) || teacherAds.length === 0) {
      return MOCK_BANNER_ADS.map((b) => ({
        ...b,
        primaryButton: b.primaryButton
          ? {
            ...b.primaryButton,
            action:
              b.primaryButton.text === "تحميل التطبيق للمعلمين"
                ? () => navigate("/apk-viewer")
                : b.primaryButton.text === "شاهد الفيديو التعريفي"
                  ? () => setShowVideo(true)
                  : () => navigate("/signin"),
          }
          : null,
        secondaryButton: b.secondaryButton
          ? { ...b.secondaryButton, action: () => setShowVideo(true) }
          : null,
      }));
    }
    let items = teacherAds;
    const platformOwnerAds = items.filter((i) => i.is_platform_owner);
    if (platformOwnerAds.length > 0) items = platformOwnerAds;
    return items.slice(0, 3).map((ad) => {
      let formattedTitle = ad.title || "إعلان من منصة امتحاني";
      if (!formattedTitle.includes("<em>")) {
        const words = formattedTitle.split(" ");
        if (words.length > 2) {
          const mid = Math.floor(words.length / 2);
          words[mid] = `<em>${words[mid]}`;
          words[words.length - 1] = `${words[words.length - 1]}</em>`;
          formattedTitle = words.join(" ");
        }
      }

      return {
        id: `server-${ad.id}`,
        image: ad.image ? getFullFileUrl(ad.image) : banner1,
        category: ad.teacher_name || "إعلان من منصة امتحاني",
        title: formattedTitle,
        description: ad.description || "اكتشف المزيد من الخدمات التعليمية المميزة.",
        primaryButton: ad.link
          ? {
            text: "اعرف المزيد",
            icon: faAd,
            action: () => {
              const hasProtocol =
                typeof ad.link === "string" &&
                (ad.link.startsWith("http://") || ad.link.startsWith("https://"));
              window.open(hasProtocol ? ad.link : `https://${ad.link}`, "_blank", "noopener,noreferrer");
            },
          }
          : null,
      };
    });
  }, [teacherAds, navigate]);

  return (
    <section className="mb-root" id="top">
      <ParticleBackground />
      {/* Video Modal */}
      <AnimatePresence>
        {showVideo && (
          <motion.div
            className="mb-video-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowVideo(false)}
          >
            <div className="mb-video-inner" onClick={(e) => e.stopPropagation()}>
              <VideoViewerV2
                url="/assets/images/Promo.mp4"
                title="منصة امتحاني - فيديو تعريفي"
                autoPlay={true}
                muted={false}
                onClose={() => setShowVideo(false)}
                className="mb-video-player"
              />
              <button
                type="button"
                className="mb-video-close"
                onClick={() => setShowVideo(false)}
                aria-label="إغلاق"
                title="إغلاق"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <Swiper
        ref={swiperRef}
        modules={[Autoplay, Pagination, EffectFade]}
        effect="fade"
        fadeEffect={{ crossFade: true }}
        loop
        speed={1000}
        autoplay={{ delay: 7000, disableOnInteraction: false, pauseOnMouseEnter: true }}
        pagination={{ clickable: true, el: ".mb-pagination" }}
        className="mb-swiper"
      >
        {bannerData.map((banner, index) => (
          <SwiperSlide key={banner.id} className="mb-slide">
            <div className="mb-container">
              {/* Left: Content Section */}
              <div className="mb-card">
                <div className="mb-card-inner">
                  <motion.div
                    className="mb-eyebrow"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    custom={0}
                    variants={fadeUp}
                  >
                    <span className="mb-eyebrow-dot" />
                    {banner.category || "منصة امتحاني"}
                  </motion.div>

                  <motion.h1
                    className="mb-title"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    custom={1}
                    variants={fadeUp}
                    dangerouslySetInnerHTML={{ __html: banner.title }}
                  />

                  <motion.p
                    className="mb-desc"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    custom={2}
                    variants={fadeUp}
                  >
                    {banner.description}
                  </motion.p>

                  <motion.div
                    className="mb-actions"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    custom={3}
                    variants={fadeUp}
                  >
                    {banner.primaryButton && (
                      <button
                        type="button"
                        className="mb-btn mb-btn--primary"
                        onClick={banner.primaryButton.action}
                      >
                        <span>{banner.primaryButton.text}</span>
                        <FontAwesomeIcon icon={banner.primaryButton.icon || faArrowLeft} />
                      </button>
                    )}
                    {banner.secondaryButton && (
                      <button
                        type="button"
                        className="mb-btn mb-btn--glass"
                        onClick={banner.secondaryButton.action}
                      >
                        <span className="mb-btn-play-icon">
                          <FontAwesomeIcon icon={banner.secondaryButton.icon || faPlay} />
                        </span>
                        <span>{banner.secondaryButton.text}</span>
                      </button>
                    )}
                  </motion.div>

                  {/* Trust badges - show only on first slide */}
                  {index === 0 && <TrustBadges studentCount={studentCount} teacherCount={teacherCount} />}
                </div>
              </div>

              {/* Right: Visual Section */}
              <motion.div
                className="mb-image-pane"
                initial={{ opacity: 0, scale: 0.92, x: -40 }}
                whileInView={{ opacity: 1, scale: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.9, ease: [0.21, 1, 0.36, 1] }}
                style={{
                  transform: `translate(${mousePos.x * 0.3}px, ${mousePos.y * 0.3}px)`,
                }}
              >
                <div className="mb-image-container">
                  <div className="mb-image-glass-frame">
                    {isVideo(banner.image) ? (
                      <VideoViewerV2
                        url={banner.image}
                        poster={videoScreen}
                        title={banner.title}
                        autoPlay={false}
                        muted={false}
                        className="mb-img"
                      />
                    ) : (
                      <img
                        src={banner.image}
                        alt="Banner Visual"
                        className="mb-img"
                        loading={index === 0 ? "eager" : "lazy"}
                      />
                    )}
                  </div>

                </div>
              </motion.div>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>

      {/* Pagination dots */}
      <div className="mb-pagination" />

      <style>{`
        /* ═══════════════════════════════════════════════
           MAIN BANNER — PREMIUM LIGHT-MODE DESIGN
           ═══════════════════════════════════════════════ */

        .mb-root {
          font-family: "Zain", sans-serif;
          direction: rtl;
          position: relative;
          width: 100%;
          min-height: 100vh;
          background: white;
          overflow: hidden;
          display: flex;
          align-items: center;
        }

        /* ── Swiper ── */
        .mb-swiper { width: 100%; height: 100%; position: relative; z-index: 5; }
        .mb-slide { background: transparent; }

        /* ── Layout Container ── */
        .mb-container {
          max-width: 1400px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: 1.15fr 0.85fr;
          gap: 72px;
          align-items: center;
          padding: 100px 48px 80px;
          min-height: 100vh;
        }

        /* ── Content Card ── */
        .mb-card { position: relative; z-index: 10; }
        .mb-card-inner { max-width: 640px; }

        .mb-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          background: #EEF2FF;
          padding: 8px 20px 8px 24px;
          border-radius: 100px;
          color: #6366f1;
          font-weight: 800;
          font-size: 0.88rem;
          letter-spacing: 0.06em;
          margin-bottom: 28px;
        }
        .mb-eyebrow-dot {
          width: 8px; height: 8px;
          border-radius: 50%;
          background: #6366f1;
        }

        .mb-title {
          font-size: clamp(2.2rem, 4.5vw, 3.8rem);
          font-weight: 900;
          color: #0f172a;
          line-height: 1.2;
          margin: 0 0 24px;
          overflow-wrap: anywhere;
          letter-spacing: -0.02em;
        }
        .mb-title em {
          font-style: normal;
          color: #6366f1;
        }

        .mb-desc {
          font-size: 1.2rem;
          color: #64748b;
          line-height: 1.85;
          margin: 0 0 44px;
          font-weight: 500;
          max-width: 540px;
        }

        /* ── Buttons ── */
        .mb-actions { display: flex; flex-wrap: wrap; gap: 16px; }

        .mb-btn {
          display: inline-flex;
          align-items: center;
          gap: 12px;
          padding: 18px 36px;
          border-radius: 18px;
          font-size: 1.05rem;
          font-weight: 800;
          font-family: inherit;
          cursor: pointer;
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          border: none;
          position: relative;
          overflow: hidden;
        }

        .mb-btn--primary {
          background: #6366f1;
          color: #fff;
        }
        .mb-btn--primary:hover {
          transform: translateY(-4px);
          background: #4f46e5;
        }
        .mb-btn--primary span,
        .mb-btn--primary svg { position: relative; z-index: 1; }

        .mb-btn--glass {
          background: #F8FAFC;
          color: #0f172a;
          border: 1px solid #E2E8F0;
        }
        .mb-btn--glass:hover {
          background: #EEF2FF;
          transform: translateY(-4px);
          border-color: #6366f1;
        }
        .mb-btn-play-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 36px; height: 36px;
          border-radius: 50%;
          background: #EEF2FF;
          color: #6366f1;
          font-size: 0.85rem;
          flex-shrink: 0;
        }

        /* ── Trust Badges ── */
        .mb-trust-row {
          display: flex;
          align-items: center;
          gap: 20px;
          margin-top: 48px;
          padding: 20px 28px;
          background: #F8FAFC;
          border: 1px solid #E2E8F0;
          border-radius: 20px;
          width: fit-content;
        }
        .mb-trust-badge {
          display: flex;
          align-items: center;
          gap: 10px;
          font-weight: 800;
          font-size: 0.95rem;
          color: #334155;
        }
        .mb-trust-icon {
          width: 36px; height: 36px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.95rem;
          background: #EEF2FF;
          color: #6366f1;
        }
        .mb-trust-sep {
          width: 1px;
          height: 28px;
          background: rgba(15,23,42,0.1);
        }

        /* ── Visual Pane ── */
        .mb-image-pane {
          position: relative;
          z-index: 8;
          transition: transform 0.15s ease-out;
        }
        .mb-image-container {
          position: relative;
        }
        .mb-image-glass-frame {
          position: relative;
          border-radius: 32px;
          overflow: hidden;
          background: white;
          border: 1px solid rgba(255,255,255,1);
          box-shadow: 0 4px 20px rgba(0,0,0,0.03);
          padding: 10px;
          aspect-ratio: 4/3;
          min-height: 460px;
        }
        .mb-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          border-radius: 24px;
        }

        /* ── Pagination ── */
        .mb-pagination {
          position: absolute;
          bottom: 36px;
          left: 50%;
          transform: translateX(-50%);
          z-index: 20;
          display: flex;
          gap: 12px;
        }
        .mb-pagination :global(.swiper-pagination-bullet) {
          width: 12px; height: 12px;
          border-radius: 50%;
          background: rgba(99, 102, 241, 0.2);
          opacity: 1;
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          cursor: pointer;
        }
        .mb-pagination :global(.swiper-pagination-bullet-active) {
          width: 36px;
          background: #6366f1;
          border-radius: 100px;
          box-shadow: 0 4px 12px rgba(245, 158, 11, 0.4);
        }

        /* ── Video Modal ── */
        .mb-video-modal {
          position: fixed; inset: 0;
          background: rgba(15, 23, 42, 0.92);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          z-index: 1000;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
        }
        .mb-video-inner {
          position: relative;
          width: 100%;
          max-width: 1000px;
          aspect-ratio: 16/9;
        }
        .mb-video-player {
          width: 100%; height: 100%;
          border-radius: 24px;
          overflow: hidden;
          box-shadow: 0 40px 80px rgba(0,0,0,0.3);
        }
        .mb-video-close {
          position: absolute;
          top: -50px;
          inset-inline-start: 0;
          z-index: 1050;
          width: 44px;
          height: 44px;
          background: rgba(15, 23, 42, 0.85);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.3);
          border-radius: 50%;
          color: #fff;
          font-size: 1.2rem;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s ease;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4);
        }
        .mb-video-close:hover {
          background: #ef4444;
          border-color: #ef4444;
          transform: scale(1.1) rotate(90deg);
        }

        /* ═══ RESPONSIVE ═══ */

        @media (max-width: 1024px) {
          .mb-root { min-height: auto; }

          .mb-container {
            grid-template-columns: 1fr;
            text-align: center;
            gap: 48px;
            padding: 80px 32px 100px;
            min-height: auto;
          }

          .mb-card-inner { max-width: 100%; margin: 0 auto; }
          .mb-desc { margin: 0 auto 40px; }
          .mb-actions { justify-content: center; }
          .mb-image-pane { order: -1; }
          .mb-image-glass-frame {
            max-width: 560px;
            margin: 0 auto;
            min-height: 340px;
          }
          .mb-trust-row {
            justify-content: center;
            margin: 40px auto 0;
          }
          .mb-grid-pattern { display: none; }
        }

        @media (max-width: 640px) {
          .mb-root { align-items: flex-start; }

          .mb-container {
            padding: 56px 20px 90px;
            gap: 32px;
          }

          .mb-title {
            font-size: 2rem;
            margin-bottom: 16px;
          }

          .mb-eyebrow {
            font-size: 0.8rem;
            padding: 6px 16px 6px 18px;
            margin-bottom: 20px;
          }

          .mb-desc {
            font-size: 1.05rem;
            margin-bottom: 32px;
          }

          .mb-actions { gap: 12px; }
          .mb-btn {
            width: 100%;
            justify-content: center;
            padding: 16px 24px;
            font-size: 1rem;
          }

          .mb-image-glass-frame {
            min-height: 240px;
            border-radius: 24px;
            padding: 6px;
          }
          .mb-img { border-radius: 20px; }

          .mb-pagination {
            bottom: 20px;
            padding: 8px 14px;
          }

          .mb-trust-row {
            flex-wrap: wrap;
            gap: 14px;
            padding: 16px 20px;
            justify-content: center;
          }
          .mb-trust-sep { display: none; }
          .mb-trust-badge { font-size: 0.88rem; }

          .mb-ambient-orb--1 { width: 300px; height: 300px; }
          .mb-ambient-orb--2 { width: 250px; height: 250px; }
          .mb-ambient-orb--3 { display: none; }
        }
      `}</style>
    </section>
  );
};

export default MainBanner;